/**
 * The der/die/das colour tokens, in one place.
 *
 * The tint (der = blue, die = red, das = green) is carried over from v1 and
 * appears on every surface that shows a noun. It had been re-derived by hand
 * in each of them; this is the single definition, extracted the same way
 * `@/lib/shuffle` was once two apps needed the seeded shuffle.
 *
 * `null` is a real case, not a defensive one: the vokabeln deck contains verbs
 * and adjectives, which have no gender and get the neutral border.
 */
/**
 * Every article any deck asks about.
 *
 * German's three and Portuguese's two live in one union because they share a
 * colour scheme on purpose: masculine is blue and feminine is red in both, so
 * someone studying both languages is not learning two palettes for one idea.
 * Portuguese simply never produces the third.
 */
export type Article = 'der' | 'die' | 'das' | 'o' | 'a';

/** The articles a deck offers, in the order they are shown. */
export const GERMAN_ARTICLES: Article[] = ['der', 'die', 'das'];
export const PORTUGUESE_ARTICLES: Article[] = ['o', 'a'];

/** Left-border colour class. Pair with a `border-l-*` width utility. */
export function articleBorder(article: Article | null): string {
  if (article === 'der' || article === 'o') return 'border-l-masculine';
  if (article === 'die' || article === 'a') return 'border-l-feminine';
  if (article === 'das') return 'border-l-neutral-gender';
  return 'border-l-border';
}

/** Text colour class for the article word itself. */
export function articleText(article: Article): string {
  if (article === 'der' || article === 'o') return 'text-masculine';
  if (article === 'die' || article === 'a') return 'text-feminine';
  return 'text-neutral-gender';
}

/** Hover colours for a pickable article button. */
export function articleHover(article: Article): string {
  if (article === 'der' || article === 'o') return 'hover:border-masculine hover:text-masculine';
  if (article === 'die' || article === 'a') return 'hover:border-feminine hover:text-feminine';
  return 'hover:border-neutral-gender hover:text-neutral-gender';
}
